import { getUserInfo } from '../data/users';
import { deleteAllFormData, storeFormData } from '../storage/indexedDB';
import { getImageDimensions, getNormalizedFile } from '../utils/fileUtils';
import { updateFormStyling, updateSentryFeedbackFormInputs } from '../utils/formUtils';
import { checkAttachedImage } from './imageHandlers';

/**
 * Handler for when the Sentry feedback form opens
 * Adds an upload image button to the form
 */
export const onFormOpen = (): void => {
  // Get the shadow host element
  const feedbackHost = document.getElementById('sentry-feedback');

  if (!feedbackHost) {
    return;
  }

  // Access the shadow root if it exists
  const shadowRoot = feedbackHost.shadowRoot;

  if (!shadowRoot) {
    return;
  }

  // Check if the upload button already exists to avoid duplication
  if (shadowRoot.querySelector('label[for="upload_image"]')) {
    return;
  }

  // Find the form__top element which contains all form inputs
  const formTopElement = shadowRoot.querySelector('.form__top');

  if (!formTopElement) {
    return;
  }

  try {
    // Create the new label element
    const newLabel = document.createElement('label');
    newLabel.setAttribute('for', 'upload_image');
    newLabel.className = 'form__label';

    // <span class="form__label__text">Description<span class="form__label__text--required">(required)</span></span>

    const span = document.createElement('span');
    span.className = 'form__label__text';
    span.textContent = 'Upload Image';
    newLabel.append(span);

    // Create button matching the form style
    const input = document.createElement('input');
    input.className = 'btn btn--default';
    input.type = 'file';
    input.style.width = '237px';
    input.accept = '.jpeg,.jpg,.png,.gif'; // Restrict to specific image formats

    const removeSpan = document.createElement('span');
    removeSpan.className = 'form__label__text';
    removeSpan.textContent = 'Remove Image';
    removeSpan.style.fontSize = '11px';
    removeSpan.style.display = 'none';

    // Handle file selection
    input.addEventListener('change', async (e: Event) => {
      const target = e.target as HTMLInputElement;
      const file = target.files?.[0];

      if (file) {
        // Convert file to base64 for processing
        const reader = new FileReader();
        const image = new Image();

        reader.onload = async (e) => {
          const base64Data = e.target?.result;
          image.src = reader.result as string;
          const fileSize = file.size;

          try {
            const dimensions = await getImageDimensions(base64Data as string);

            const finalFile = {
              attachment: file,
              file_name: new Date().getTime().toString(),
              file_width: dimensions.width.toString(),
              file_height: dimensions.height.toString(),
              file_type: 'image',
              file_size: fileSize.toString(),
              tag_list: '',
            };

            const formData = new FormData();
            formData.append('attachment', finalFile.attachment);
            formData.append('file_name', finalFile.file_name);
            formData.append('file_width', finalFile.file_width.toString());
            formData.append('file_height', finalFile.file_height.toString());
            formData.append('file_size', finalFile.file_size);
            formData.append('file_type', finalFile.file_type);
            formData.append('tag_list', finalFile.tag_list);
            formData.append('media_bin', 'true');

            // Store FormData in IndexedDB
            await storeFormData(formData);

            removeSpan.style.display = 'block';
          } catch (error) {
            // Handle error silently to prevent app crashes
          }
        };

        try {
          const normalizedFile = await getNormalizedFile(file);
          reader.readAsDataURL(normalizedFile);
        } catch (error) {
          // Handle error silently to prevent app crashes
        }
      } else {
        // No file selected, re-enable the button
      }
    });

    newLabel.appendChild(input);

    removeSpan.addEventListener('click', async (e: Event) => {
      deleteAllFormData();
      input.value = '';
      removeSpan.style.display = 'none';
    });

    newLabel.appendChild(removeSpan);

    // Append the new label after the form__top's last child
    formTopElement.appendChild(newLabel);

    const { email, firstName, lastName } = getUserInfo();
    updateSentryFeedbackFormInputs(email, firstName, lastName);
    updateFormStyling();

    // Check for existing attached images
    setTimeout(() => {
      checkAttachedImage();
    }, 100);
  } catch (error) {
    // Silently handle errors to prevent app crashes
  }
};
